// src/features/monetize/schemas/monetizeSchemas.ts

import { z } from 'zod';
import { COMP_MODES, MONETIZE_CONSTRAINTS, OFFER_DURATIONS, PRICE_INTERVALS, PRODUCT_TYPES } from '@/lib/constants/monetize';

const numberOrEmpty = z.union([z.number(), z.literal('')]);

const amountField = numberOrEmpty.refine(
  (v) => typeof v === 'number' && Math.round(v * 100) >= MONETIZE_CONSTRAINTS.MIN_AMOUNT_CENTS,
  { message: 'At least $0.50' },
);

export const priceFormSchema = z.object({
  amount: amountField,
  interval: z.enum(PRICE_INTERVALS),
  trialDays: z.number().int().min(0).max(MONETIZE_CONSTRAINTS.MAX_TRIAL_DAYS, `At most ${MONETIZE_CONSTRAINTS.MAX_TRIAL_DAYS} days`),
  nickname: z.string().trim().max(MONETIZE_CONSTRAINTS.NICKNAME_MAX),
});
export type PriceFormValues = z.infer<typeof priceFormSchema>;

export const productFormSchema = z
  .object({
    type: z.enum(PRODUCT_TYPES),
    name: z.string().trim().min(1, 'Name is required').max(MONETIZE_CONSTRAINTS.NAME_MAX),
    description: z.string().trim().max(MONETIZE_CONSTRAINTS.DESCRIPTION_MAX),
    active: z.boolean(),
    wholeService: z.boolean(),
    scopes: z.array(z.string()).max(MONETIZE_CONSTRAINTS.MAX_SCOPES),
    accessStartsAt: z.string().nullable(),
    accessEndsAt: z.string().nullable(),
    rentalWindowHours: numberOrEmpty,
    onSaleFrom: z.string().nullable(),
    onSaleUntil: z.string().nullable(),
    maxConcurrentStreams: z.number().int().min(MONETIZE_CONSTRAINTS.MIN_CONCURRENT).max(MONETIZE_CONSTRAINTS.MAX_CONCURRENT),
    prices: z.array(priceFormSchema).max(MONETIZE_CONSTRAINTS.MAX_PRICES),
  })
  .superRefine((v, ctx) => {
    if (!v.wholeService && v.scopes.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['scopes'], message: 'Pick at least one playlist or item, or sell everything' });
    }
    if (v.type === 'rental' && (typeof v.rentalWindowHours !== 'number' || v.rentalWindowHours < 1)) {
      ctx.addIssue({ code: 'custom', path: ['rentalWindowHours'], message: 'How long a rental plays, in hours' });
    }
    if (v.type === 'pass' && v.accessStartsAt && v.accessEndsAt && v.accessEndsAt <= v.accessStartsAt) {
      ctx.addIssue({ code: 'custom', path: ['accessEndsAt'], message: 'Must be after the start' });
    }
    if (v.onSaleFrom && v.onSaleUntil && v.onSaleUntil <= v.onSaleFrom) {
      ctx.addIssue({ code: 'custom', path: ['onSaleUntil'], message: 'Must be after the start' });
    }
    v.prices.forEach((p, i) => {
      if (v.type === 'subscription' && p.interval === 'one_time') {
        ctx.addIssue({ code: 'custom', path: ['prices', i, 'interval'], message: 'A subscription recurs' });
      }
      if (v.type !== 'subscription' && p.interval !== 'one_time') {
        ctx.addIssue({ code: 'custom', path: ['prices', i, 'interval'], message: 'Bought once' });
      }
    });
  });
export type ProductFormValues = z.infer<typeof productFormSchema>;

export const offerFormSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(MONETIZE_CONSTRAINTS.CODE_MIN, `${MONETIZE_CONSTRAINTS.CODE_MIN} to ${MONETIZE_CONSTRAINTS.CODE_MAX} letters or digits`)
      .max(MONETIZE_CONSTRAINTS.CODE_MAX)
      .regex(/^[A-Za-z0-9]+$/, 'Letters and digits only'),
    name: z.string().trim().min(1, 'Name is required').max(MONETIZE_CONSTRAINTS.NAME_MAX),
    discountType: z.enum(['percent', 'amount']),
    percentOff: z.number().int().min(1).max(100),
    amountOff: numberOrEmpty,
    duration: z.enum(OFFER_DURATIONS),
    durationMonths: z.number().int().min(1).max(MONETIZE_CONSTRAINTS.MAX_OFFER_MONTHS),
    productId: z.string(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    maxRedemptions: numberOrEmpty,
  })
  .superRefine((v, ctx) => {
    if (v.discountType === 'amount' && (typeof v.amountOff !== 'number' || v.amountOff <= 0)) {
      ctx.addIssue({ code: 'custom', path: ['amountOff'], message: 'An amount off, in dollars' });
    }
    if (v.startsAt && v.endsAt && v.endsAt <= v.startsAt) {
      ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'Must be after the start' });
    }
  });
export type OfferFormValues = z.infer<typeof offerFormSchema>;

export const membershipFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(MONETIZE_CONSTRAINTS.NAME_MAX),
    description: z.string().trim().max(MONETIZE_CONSTRAINTS.DESCRIPTION_MAX),
    wholeService: z.boolean(),
    scopes: z.array(z.string()),
    monthlyAmount: amountField,
    yearlyAmount: numberOrEmpty,
    trialDays: z.number().int().min(0).max(MONETIZE_CONSTRAINTS.MAX_TRIAL_DAYS),
  })
  .superRefine((v, ctx) => {
    if (!v.wholeService && v.scopes.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['scopes'], message: 'Pick the playlists or items members get, or include everything' });
    }
    if (typeof v.yearlyAmount === 'number' && Math.round(v.yearlyAmount * 100) < MONETIZE_CONSTRAINTS.MIN_AMOUNT_CENTS) {
      ctx.addIssue({ code: 'custom', path: ['yearlyAmount'], message: 'At least $0.50, or leave empty' });
    }
  });
export type MembershipFormValues = z.infer<typeof membershipFormSchema>;

export const ticketedLiveFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(MONETIZE_CONSTRAINTS.NAME_MAX),
    description: z.string().trim().max(MONETIZE_CONSTRAINTS.DESCRIPTION_MAX),
    liveMediaId: z.string().min(1, 'Pick the live stream'),
    listIds: z.array(z.string()),
    eventStartsAt: z.string().nullable(),
    eventEndsAt: z.string().nullable(),
    replayHours: z.number().int().min(0).max(MONETIZE_CONSTRAINTS.MAX_REPLAY_HOURS),
    amount: amountField,
    promoCode: z.string().trim().max(MONETIZE_CONSTRAINTS.CODE_MAX).regex(/^[A-Za-z0-9]*$/, 'Letters and digits only'),
    promoPercentOff: z.number().int().min(0).max(100),
  })
  .superRefine((v, ctx) => {
    if (v.replayHours > 0 && !v.eventEndsAt) {
      ctx.addIssue({ code: 'custom', path: ['eventEndsAt'], message: 'A replay window needs the event end' });
    }
    if (v.eventStartsAt && v.eventEndsAt && v.eventEndsAt <= v.eventStartsAt) {
      ctx.addIssue({ code: 'custom', path: ['eventEndsAt'], message: 'Must be after the start' });
    }
    if ((v.promoCode === '') !== (v.promoPercentOff === 0)) {
      ctx.addIssue({ code: 'custom', path: ['promoCode'], message: 'A code and its percent off go together' });
    }
    if (v.promoCode !== '' && v.promoCode.length < MONETIZE_CONSTRAINTS.CODE_MIN) {
      ctx.addIssue({ code: 'custom', path: ['promoCode'], message: `At least ${MONETIZE_CONSTRAINTS.CODE_MIN} characters` });
    }
  });
export type TicketedLiveFormValues = z.infer<typeof ticketedLiveFormSchema>;

export const profileFormSchema = z.object({
  brandName: z.string().trim().min(1, 'The name viewers see is required').max(MONETIZE_CONSTRAINTS.NAME_MAX),
  supportEmail: z.string().trim().email('An e-mail address'),
});
export type ProfileFormValues = z.infer<typeof profileFormSchema>;

// ---- the viewer's ledger (Seq 1 step 4) ----

const reasonField = z.string().trim().min(1, 'Say why; it goes in the history').max(MONETIZE_CONSTRAINTS.REASON_MAX, `At most ${MONETIZE_CONSTRAINTS.REASON_MAX} characters`);

export const compFormSchema = z
  .object({
    wholeService: z.boolean(),
    scopes: z.array(z.string()).max(MONETIZE_CONSTRAINTS.MAX_SCOPES),
    accessMode: z.enum(COMP_MODES),
    endsAt: z.string().nullable(),
    reason: reasonField,
  })
  .superRefine((v, ctx) => {
    if (!v.wholeService && v.scopes.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['scopes'], message: 'Pick what the viewer gets, or grant everything' });
    }
  });
export type CompFormValues = z.infer<typeof compFormSchema>;

export const reasonFormSchema = z.object({ reason: reasonField });
export type ReasonFormValues = z.infer<typeof reasonFormSchema>;

export const extendFormSchema = z.object({
  endsAt: z.string().min(1, 'When access should end'),
  reason: reasonField,
});
export type ExtendFormValues = z.infer<typeof extendFormSchema>;

/** The refund form, bounded by what is still refundable (cents). */
export function refundFormSchema(refundableCents: number) {
  return z
    .object({
      mode: z.enum(['full', 'partial']),
      amount: numberOrEmpty,
      reason: reasonField,
    })
    .superRefine((v, ctx) => {
      if (v.mode !== 'partial') return;
      const cents = typeof v.amount === 'number' ? Math.round(v.amount * 100) : 0;
      if (cents < 1) {
        ctx.addIssue({ code: 'custom', path: ['amount'], message: 'An amount in dollars' });
      } else if (cents > refundableCents) {
        ctx.addIssue({ code: 'custom', path: ['amount'], message: `At most $${(refundableCents / 100).toFixed(2)}` });
      }
    });
}
export type RefundFormValues = z.infer<ReturnType<typeof refundFormSchema>>;

export const mailSenderFormSchema = z.object({
  fromLocal: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'The part before the @')
    .max(64)
    .regex(/^[a-z0-9][a-z0-9._+-]*$/, 'Letters, digits, dots, dashes, plus or underscore'),
  domain: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'A domain you control, such as tv.example.com')
    .max(253)
    .regex(/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'A domain name, such as tv.example.com'),
});
export type MailSenderFormValues = z.infer<typeof mailSenderFormSchema>;

export const cancelFormSchema = z.object({
  when: z.enum(['period_end', 'now']),
  refundLastPayment: z.boolean(),
  reason: reasonField,
});
export type CancelFormValues = z.infer<typeof cancelFormSchema>;
